import { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, Plus } from "lucide-react";
import { ARTICLES, SECTIONS, getAuthor, getSection, articlesBySection, relatedArticles } from "@/lib/news";
import MarketsModule from "./MarketsModule";
import WeatherModule from "./WeatherModule";

const PAGE_SIZE = 6;

export function NewsV1Section() {
  const { slug } = useParams();
  const [visible, setVisible] = useState(PAGE_SIZE);
  const section = getSection(slug);
  if (!section || section.slug !== slug) return <Navigate to="/" replace/>;
  const list = articlesBySection(slug);
  const shown = list.slice(0, visible);
  const hasMore = visible < list.length;
  return (
    <div data-testid="newsv1-section" className="px-4 lg:px-8 py-6">
      <div className="rule-section mb-3 flex items-baseline justify-between flex-wrap gap-2">
        <Link to="/" className="text-[11px] font-cond hover:text-[var(--accent)] flex items-center gap-1"><ArrowLeft size={11}/> Home</Link>
        <span className="pill-section">{section.name}</span>
        <span className="text-[10px] text-[var(--fg-mute)] font-mono" data-testid="section-count">{list.length} stories</span>
      </div>
      <h1 className="text-headline text-4xl lg:text-6xl">{section.name}.</h1>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-6 col-rule" data-testid="section-grid">
        {shown.map(a => {
          const author = getAuthor(a.author);
          return (
            <article key={a.slug} className="md:pl-5 first:md:pl-0" data-testid={`section-card-${a.slug}`}>
              <Link to={`/article/${a.slug}`} className="block aspect-[16/10] overflow-hidden mb-2 hover-lift"><img src={a.cover} alt="" className="w-full h-full object-cover"/></Link>
              <span className="pill-section">{a.subtag}</span>
              {a.premium && <span className="premium-tag ml-1">★ Subscriber</span>}
              <h2 className="text-headline text-xl lg:text-2xl mt-1 underline-on-hover"><Link to={`/article/${a.slug}`}>{a.title}</Link></h2>
              <p className="font-italic text-sm text-[var(--fg-mute)] mt-1.5 line-clamp-2">{a.dek}</p>
              <p className="text-[11px] flex items-center gap-1.5 text-[var(--fg-mute)] mt-1.5">
                <img src={author.img} alt="" className="w-4 h-4 rounded-full object-cover"/>
                <span><span className="font-display text-[var(--fg)]">{author.name}</span> · {a.date} · <span style={{color:"var(--accent)"}}>{a.read}</span></span>
              </p>
            </article>
          );
        })}
      </div>
      {hasMore && (
        <div className="mt-8 flex justify-center">
          <button
            onClick={() => setVisible(v => v + PAGE_SIZE)}
            data-testid="load-more-btn"
            className="inline-flex items-center gap-2 px-8 py-3 font-cond text-[12px] tracking-[0.22em] hover:opacity-90 hover:translate-y-[-2px] transition"
            style={{background:"var(--accent)", color:"var(--accent-fg)"}}
          >
            <Plus size={14}/> LOAD MORE ({list.length - visible} MORE)
          </button>
        </div>
      )}
      {!hasMore && list.length > PAGE_SIZE && (
        <p className="mt-8 text-center text-[11px] font-cond" style={{color:"var(--fg-mute)"}}>— End of {section.name} —</p>
      )}
    </div>
  );
}

export function NewsV1Article() {
  const { slug } = useParams();
  const article = ARTICLES.find(a => a.slug === slug);
  if (!article) return <Navigate to="/" replace/>;
  const author = getAuthor(article.author);
  const section = getSection(article.section);
  const related = relatedArticles(article, 3);

  return (
    <div data-testid="newsv1-article">
      {/* breadcrumb */}
      <section className="border-b px-4 lg:px-8 py-2.5 flex items-center gap-3 flex-wrap" style={{borderColor:"var(--line-soft)", background:"var(--bg-elev)"}}>
        <Link to="/" className="text-[11px] font-cond hover:text-[var(--accent)] flex items-center gap-1"><ArrowLeft size={11}/> Home</Link>
        <span className="text-[10px] text-[var(--fg-mute)]">/</span>
        <Link to={`/section/${section.slug}`} className="text-[11px] font-cond hover:text-[var(--accent)]">{section.name}</Link>
        <span className="text-[10px] text-[var(--fg-mute)]">/</span>
        <span className="text-[11px] font-cond text-[var(--fg-mute)] truncate">{article.subtag}</span>
      </section>

      {/* hero */}
      <section className="border-b px-4 lg:px-8 py-7" style={{borderColor:"var(--line)"}}>
        <div className="max-w-4xl mx-auto">
          <div className="flex items-baseline gap-3 flex-wrap">
            <span className="pill-section">{article.tag}</span>
            <span className="font-mono text-[10px] text-[var(--fg-mute)]">{article.subtag}</span>
            <span className="font-mono text-[10px] text-[var(--fg-mute)]">· {article.date}</span>
            <span className="font-mono text-[10px] text-[var(--fg-mute)]">· {article.read}</span>
            {article.premium && <span className="premium-tag">★ Subscriber</span>}
          </div>
          <h1 className="text-headline text-3xl sm:text-4xl lg:text-6xl mt-3">{article.title}</h1>
          <p className="font-italic text-lg lg:text-2xl text-[var(--fg-mute)] mt-3 max-w-3xl">{article.dek}</p>
          <div className="flex items-center gap-3 mt-5 pb-5 border-b" style={{borderColor:"var(--line-soft)"}}>
            <img src={author.img} alt={author.name} className="w-10 h-10 rounded-full object-cover border-2" style={{borderColor:"var(--accent)"}}/>
            <div className="flex-1">
              <p className="text-sm"><span className="font-display">{author.name}</span></p>
              <p className="font-mono text-[10px] text-[var(--fg-mute)]">{author.role}</p>
            </div>
          </div>
        </div>
      </section>

      {/* cover */}
      <section className="border-b px-4 lg:px-8 py-5" style={{borderColor:"var(--line)"}}>
        <figure className="max-w-5xl mx-auto">
          <div className="aspect-[16/9] overflow-hidden"><img src={article.cover} alt={article.coverAlt} className="w-full h-full object-cover"/></div>
          <figcaption className="text-[10px] text-[var(--fg-mute)] italic mt-2">{article.coverAlt}</figcaption>
        </figure>
      </section>

      {/* body */}
      <section className="border-b px-4 lg:px-8 py-8" style={{borderColor:"var(--line)"}}>
        <div className="max-w-5xl mx-auto grid grid-cols-12 gap-x-6 gap-y-5">
          <article className="col-span-12 lg:col-span-8 prose-content">
            {article.body.map((b, i) => {
              const node = b.kind === "lede" ? <p key={i} className="dropcap text-base leading-relaxed mb-4">{b.text}</p>
                : b.kind === "h2" ? <h2 key={i} className="text-headline text-2xl lg:text-3xl mt-7 mb-3 rule-section">{b.text}</h2>
                : b.kind === "pull" ? <blockquote key={i} className="my-5 border-l-4 py-3 px-4" style={{borderColor:"var(--accent)", background:"var(--bg-elev)"}}><p className="font-italic text-xl lg:text-2xl leading-snug">"{b.text}"</p></blockquote>
                : <p key={i} className="text-base leading-relaxed mb-3.5">{b.text}</p>;
              const imgIdx = [2, 6, 10, 14, 18].indexOf(i);
              if (imgIdx >= 0 && article.images?.[imgIdx]) {
                return (
                  <div key={`b-${i}`}>{node}
                    <figure className="my-5">
                      <div className="aspect-[16/10] overflow-hidden"><img src={article.images[imgIdx]} alt="" className="w-full h-full object-cover"/></div>
                      <figcaption className="text-[10px] text-[var(--fg-mute)] italic mt-1.5">Photograph · World Media Journal · {article.date}</figcaption>
                    </figure>
                  </div>
                );
              }
              return node;
            })}
          </article>
          <aside className="col-span-12 lg:col-span-4 lg:border-l lg:pl-5 space-y-4" style={{borderColor:"var(--line-soft)"}}>
            <div className="border p-3" style={{borderColor:"var(--accent)", background:"var(--bg-elev)"}}>
              <p className="font-cond text-[10px] mb-1" style={{color:"var(--accent)"}}>In this story</p>
              <ol className="space-y-1.5 text-xs">
                {article.body.filter(b=>b.kind==="h2").map((b,i)=>(<li key={i} className="flex gap-2"><span className="font-mono shrink-0" style={{color:"var(--accent)"}}>{String(i+1).padStart(2,"0")}</span>{b.text}</li>))}
              </ol>
            </div>

            {/* MORE STORIES — previews of other posts */}
            <div className="border-y" style={{borderColor:"var(--line)", borderTopWidth:3, borderBottomWidth:3}} data-testid="article-more-stories">
              <div className="flex items-baseline justify-between py-3">
                <h3 className="font-display text-xl">More stories</h3>
                <span className="font-cond text-[10px] tracking-[0.18em]" style={{color:"var(--fg-mute)"}}>TODAY</span>
              </div>
              <ul>
                {ARTICLES.filter(a => a.slug !== article.slug).slice(0, 6).map(a => (
                  <li key={a.slug} className="grid grid-cols-12 gap-2.5 py-3 border-t" style={{borderColor:"var(--line-soft)"}}>
                    <Link to={`/article/${a.slug}`} className="col-span-4 aspect-[4/3] overflow-hidden hover-lift">
                      <img src={a.cover} alt="" className="w-full h-full object-cover"/>
                    </Link>
                    <div className="col-span-8">
                      <span className="text-[9px] font-mono uppercase tracking-wider" style={{color:"var(--accent)"}}>{a.tag} · {a.subtag}</span>
                      <h4 className="font-display text-[13px] leading-tight mt-0.5 line-clamp-3">
                        <Link to={`/article/${a.slug}`} className="underline-on-hover">{a.title}</Link>
                      </h4>
                      <p className="text-[10px] mt-1" style={{color:"var(--fg-mute)"}}>{getAuthor(a.author).name.split(" ")[0]} · {a.read}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="p-3" style={{background:"var(--accent)", color:"var(--accent-fg)"}}>
              <p className="font-cond text-[10px] mb-1">Subscribe</p>
              <p className="font-display text-base leading-tight">Today's edition, every morning.</p>
              <Link to="/contact" className="font-cond text-[10px] mt-2 inline-flex items-center gap-1 underline">Reserve a seat →</Link>
            </div>

            {/* MARKETS + WEATHER — live sidebar modules under Subscribe */}
            <MarketsModule/>
            <WeatherModule/>
          </aside>
        </div>
      </section>

      {/* related */}
      <section className="border-b px-4 lg:px-8 py-7" style={{borderColor:"var(--line)"}}>
        <div className="rule-section mb-3"><p className="text-[10px] smcaps" style={{color:"var(--accent)"}}>More from {section.name}</p></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-5 gap-y-4 col-rule">
          {related.map(a => (
            <article key={a.slug} className="md:pl-5 first:md:pl-0">
              <Link to={`/article/${a.slug}`} className="block aspect-[5/4] overflow-hidden mb-2 hover-lift"><img src={a.cover} alt="" className="w-full h-full object-cover"/></Link>
              <span className="pill-section">{a.subtag}</span>
              <h3 className="font-display text-base mt-1"><Link to={`/article/${a.slug}`} className="underline-on-hover">{a.title}</Link></h3>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

export function NewsV1Contact() {
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [error, setError] = useState("");
  const categories = [
    "Product enquiries",
    "News tips",
    "Advertising & sponsorship",
    "Business enquiries",
    "Editorial feedback",
    "Corrections",
    "Media requests",
    "Partnership opportunities",
    "Write for us — submit a story or op-ed",
  ];

  async function handleSubmit(e) {
    e.preventDefault();
    const f = e.target;
    setStatus("sending");
    setError("");
    try {
      const body = {
        first_name: f.first_name.value.trim(),
        last_name: f.last_name.value.trim(),
        email: f.email.value.trim(),
        enquiry_type: f.enquiry_type.value,
        message: f.message.value.trim(),
      };
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `Server returned ${res.status}`);
      }
      setStatus("success");
      f.reset();
    } catch (err) {
      setError(err.message || "Something went wrong.");
      setStatus("error");
    }
  }

  return (
    <div data-testid="newsv1-contact" className="max-w-5xl mx-auto px-4 lg:px-8 py-12">
      <div className="rule-section mb-3"><p className="text-[10px] smcaps" style={{color:"var(--accent)"}}>Contact · Write for us</p></div>
      <h1 className="text-headline text-4xl lg:text-6xl leading-[1.02]">
        Write for us. <span className="font-italic" style={{color:"var(--accent)"}}>Share your story.</span>
      </h1>
      <p className="font-italic text-base lg:text-xl text-[var(--fg-mute)] mt-4 max-w-3xl leading-snug">
        Whether you have a question, a news tip, a business enquiry or would like more information, we&apos;re here to help. Please complete the enquiry form and a member of the World Media Journal team will respond as soon as possible.
      </p>

      <div className="grid grid-cols-12 gap-8 mt-10">
        {/* LEFT — categories */}
        <div className="col-span-12 md:col-span-5">
          <p className="text-[10px] smcaps mb-3" style={{color:"var(--accent2)"}}>What can we help with?</p>
          <ul className="border-t" style={{borderColor:"var(--line-soft)"}}>
            {categories.map((c) => (
              <li key={c} className="py-3 border-b flex items-baseline gap-3" style={{borderColor:"var(--line-soft)"}} data-testid={`contact-cat-${c.split(" ")[0].toLowerCase()}`}>
                <span className="font-mono text-[10px] shrink-0" style={{color:"var(--accent)"}}>·</span>
                <span className="font-display text-[15px] leading-tight">{c}</span>
              </li>
            ))}
          </ul>
          <div className="mt-6 p-4 border-2" style={{borderColor:"var(--accent)", background:"var(--bg-elev)"}}>
            <p className="text-[10px] smcaps mb-1" style={{color:"var(--accent)"}}>How we reply</p>
            <p className="font-display text-lg leading-tight">Every enquiry reaches the newsroom.</p>
            <p className="text-[12px] mt-1.5" style={{color:"var(--fg-mute)"}}>Submit the form and we&apos;ll typically respond within one business day.</p>
          </div>
        </div>

        {/* RIGHT — enquiry form */}
        <div className="col-span-12 md:col-span-7 md:border-l md:pl-8" style={{borderColor:"var(--line-soft)"}}>
          <p className="text-[10px] smcaps mb-3" style={{color:"var(--accent)"}}>Enquiry form</p>
          <form className="space-y-5" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="text-[11px] smcaps" style={{color:"var(--fg-mute)"}}>First name *</label>
                <input name="first_name" data-testid="contact-first-name" required className="w-full bg-transparent border-b py-2 text-sm focus:outline-none focus:border-[var(--accent)]" style={{borderColor:"var(--line)"}}/>
              </div>
              <div>
                <label className="text-[11px] smcaps" style={{color:"var(--fg-mute)"}}>Last name *</label>
                <input name="last_name" data-testid="contact-last-name" required className="w-full bg-transparent border-b py-2 text-sm focus:outline-none focus:border-[var(--accent)]" style={{borderColor:"var(--line)"}}/>
              </div>
            </div>
            <div>
              <label className="text-[11px] smcaps" style={{color:"var(--fg-mute)"}}>Email *</label>
              <input name="email" data-testid="newsv1-email" required type="email" className="w-full bg-transparent border-b py-2 text-sm focus:outline-none focus:border-[var(--accent)]" style={{borderColor:"var(--line)"}}/>
            </div>
            <div>
              <label className="text-[11px] smcaps" style={{color:"var(--fg-mute)"}}>Type of enquiry *</label>
              <select name="enquiry_type" data-testid="contact-enquiry-type" required className="w-full bg-transparent border-b py-2 text-sm focus:outline-none focus:border-[var(--accent)]" style={{borderColor:"var(--line)"}} defaultValue="">
                <option value="" disabled>Choose one…</option>
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] smcaps" style={{color:"var(--fg-mute)"}}>Enquiry details *</label>
              <textarea name="message" data-testid="contact-message" required rows={5} className="w-full bg-transparent border-b py-2 text-sm focus:outline-none focus:border-[var(--accent)] resize-none" style={{borderColor:"var(--line)"}} placeholder="If you're pitching a story, include a one-line summary, a suggested angle, and any relevant experience or clips."/>
            </div>
            <div className="flex items-baseline justify-between flex-wrap gap-3 pt-2">
              <p className="text-[10px]" style={{color:"var(--fg-mute)"}}>By submitting you agree to our editorial standards.</p>
              <button data-testid="newsv1-submit" type="submit" disabled={status==="sending"} className="inline-flex items-center gap-2 px-6 py-3 font-cond text-[12px] tracking-[0.22em] hover:opacity-90 hover:translate-y-[-2px] transition disabled:opacity-50" style={{background:"var(--accent)", color:"var(--accent-fg)"}}>
                {status === "sending" ? "SENDING…" : "SUBMIT ENQUIRY →"}
              </button>
            </div>
            {status === "success" && (
              <p data-testid="newsv1-success" className="text-sm p-4 border-2 font-italic" style={{borderColor:"var(--accent)", background:"var(--bg-elev)"}}>
                Thank you. Your enquiry has been received. A member of the World Media Journal team will respond as soon as possible.
              </p>
            )}
            {status === "error" && (
              <p data-testid="newsv1-error" className="text-sm p-4 border-2 font-italic" style={{borderColor:"var(--accent)", background:"var(--bg-elev)", color:"var(--accent)"}}>
                We couldn&apos;t submit your enquiry right now. Please try again shortly. ({error})
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
